import { useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { Alert, Flex, FlexColumn } from '@elementary/common-ui/components';
import {
  SectionTitle,
  SectionDescription,
} from '@elementary/common-ui/components/FormControls';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONNECTION_HEALTH } from '@elementary/common-ui/constants/queryKeys';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import {
  fetchConnectionHealth,
  revalidateConnectionHealth,
} from 'shared/services/connectionHealthApi';
import { PermissionCheck } from 'shared/types/connectionHealth';

const Wrapper = styled(FlexColumn)`
  gap: 16px;
`;

const SummaryRow = styled(Flex)`
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatItem = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 13px;
`;

const RecheckButton = styled(Button)`
  && {
    font-size: 13px;
    text-transform: none;
    min-width: 0;
    padding: 0;
  }
`;

const StatusDot = styled(Box)<{ $color: 'green' | 'red' | 'gray' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color, theme }) =>
    $color === 'green'
      ? theme.palette.common.green100
      : $color === 'red'
      ? theme.palette.common.red100
      : theme.palette.common.gray2};
`;

const DetailsToggle = styled(Typography)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ChecksList = styled(FlexColumn)`
  gap: 4px;
`;

const CheckRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const GrantCommand = styled(Box)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray2};
  background: ${({ theme }) => theme.palette.common.gray6};
  padding: 6px 10px;
  border-radius: 4px;
  margin-left: 16px;

  > * + * {
    margin-top: 8px;
  }

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }

  li {
    word-break: break-all;
  }

  code {
    font-family: inherit;
  }
`;

const ACCOUNT_LEVEL_SCOPE_ROWS: [null] = [null];

const ALL_PERMISSION_CHECKS_PASSING_MESSAGE =
  'All permission checks are passing.';
const DISCONNECTED_FALLBACK_MESSAGE =
  'Cannot connect to the data warehouse. Verify credentials, user configuration, and IP allowlist.';

// Friendly labels for the permission_type slugs the backend sends.
// Anything unmapped falls back to Title Case.
const PERMISSION_DISPLAY_NAMES: Record<string, string> = {
  metadata_viewer: 'Metadata Viewer',
  resource_viewer: 'Resource Viewer',
  query_history: 'Query History',
  table_lineage: 'Table Lineage',
  billing: 'Billing',
  information_schema: 'Information Schema',
  table_and_column_metadata: 'Table & Column Metadata',
  copy_history: 'Copy History',
  column_metadata: 'Column Metadata',
};

const toTitleCase = (name: string): string =>
  name
    .replace(/[_.]/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatPermissionName = (name: string): string =>
  PERMISSION_DISPLAY_NAMES[name] ?? toTitleCase(name);

// A check covers each of its scopes (projects/databases), or one account-level
// resource when the warehouse has no per-scope breakdown (e.g. Snowflake).
const checkWeight = (check: PermissionCheck): number =>
  check.scopes.length || 1;

const getTotalPermissionChecks = (checks: PermissionCheck[]): number =>
  checks.reduce((sum, check) => sum + checkWeight(check), 0);

type Props = {
  environmentId: string;
};

type StatusStatProps = {
  color: 'green' | 'red' | 'gray';
  children: ReactNode;
};

const StatusStat = ({ color, children }: StatusStatProps) => (
  <StatItem>
    <StatusDot $color={color} />
    {children}
  </StatItem>
);

type RecheckButtonProps = {
  isRechecking: boolean;
  onClick: () => void;
};

const RecheckButtonComponent = ({
  isRechecking,
  onClick,
}: RecheckButtonProps) => (
  <RecheckButton
    variant="text"
    size="small"
    onClick={onClick}
    disabled={isRechecking}
    startIcon={
      isRechecking ? <CircularProgress size={12} color="inherit" /> : undefined
    }
  >
    {isRechecking ? 'Re-checking…' : 'Re-check now'}
  </RecheckButton>
);

const NotValidatedState = ({ isRechecking, onClick }: RecheckButtonProps) => (
  <FlexColumn sx={{ gap: '8px' }}>
    <SummaryRow>
      <StatusStat color="gray">
        <Typography sx={{ fontSize: '13px', color: 'common.gray2' }}>
          Not validated yet
        </Typography>
      </StatusStat>
      <Box sx={{ ml: 'auto' }}>
        <RecheckButtonComponent isRechecking={isRechecking} onClick={onClick} />
      </Box>
    </SummaryRow>
    <Typography sx={{ fontSize: '13px', color: 'common.gray2' }}>
      Permission checks haven&apos;t run for this connection yet. They run
      automatically during your next scheduled run, or you can re-check now.
    </Typography>
  </FlexColumn>
);

type CheckRowsProps = {
  checks: PermissionCheck[];
};

const CheckRows = ({ checks }: CheckRowsProps) => (
  <ChecksList>
    {checks.map((check, index) => {
      const scopeRows =
        check.scopes.length > 0 ? check.scopes : ACCOUNT_LEVEL_SCOPE_ROWS;
      const hasGrantCommands = check.grant_commands.length > 0;
      const hasGrantRequests = check.grant_requests.length > 0;
      const shouldShowGrantRemediation =
        !check.has_access && (hasGrantCommands || hasGrantRequests);

      return (
        <FlexColumn
          key={`${check.permission_type}-${index}`}
          sx={{ gap: '4px' }}
        >
          {scopeRows.map((scope, scopeIndex) => (
            <CheckRow
              key={`${check.permission_type}-${
                scope ?? 'account'
              }-${scopeIndex}`}
            >
              <StatusDot $color={check.has_access ? 'green' : 'red'} />
              <Typography sx={{ fontSize: '13px' }}>
                {formatPermissionName(check.permission_type)}
                {scope != null && ` · ${scope}`}
              </Typography>
            </CheckRow>
          ))}
          {shouldShowGrantRemediation && (
            <GrantCommand>
              {hasGrantCommands && (
                <div>
                  <div>
                    {`Ask your administrator to run the following ${
                      check.grant_commands.length === 1 ? 'command' : 'commands'
                    }:`}
                  </div>
                  <ul>
                    {check.grant_commands.map((command, commandIndex) => (
                      <li
                        key={`${check.permission_type}-command-${commandIndex}`}
                      >
                        <code>{command}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {check.grant_requests.map((request, requestIndex) => (
                <div
                  key={`${check.permission_type}-request-${request.role_id}-${requestIndex}`}
                >
                  <div>
                    {`Ask your administrator to grant the elementary user the role ${request.role_display_name} (${request.role_id}) for the projects:`}
                  </div>
                  <ul>
                    {request.databases.map((database) => (
                      <li key={database}>{database}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </GrantCommand>
          )}
        </FlexColumn>
      );
    })}
  </ChecksList>
);

type HealthyIssuesStateProps = {
  checks: PermissionCheck[];
  totalIssues: number;
  isRechecking: boolean;
  onRecheck: () => void;
  showDetails: boolean;
  onToggleDetails: () => void;
};

const HealthyIssuesState = ({
  checks,
  totalIssues,
  isRechecking,
  onRecheck,
  showDetails,
  onToggleDetails,
}: HealthyIssuesStateProps) => {
  const totalChecks = getTotalPermissionChecks(checks);
  const hasIssues = totalIssues > 0;
  const failingPermissionChecksMessage = `${totalIssues} of ${pluralizeString(
    totalChecks,
    'permission check',
    'permission checks',
  )} ${totalIssues === 1 ? 'is' : 'are'} failing.`;

  return (
    <>
      <SummaryRow>
        <StatusStat color={hasIssues ? 'red' : 'green'}>
          <Typography sx={{ fontSize: '14px' }}>
            {hasIssues
              ? failingPermissionChecksMessage
              : ALL_PERMISSION_CHECKS_PASSING_MESSAGE}
          </Typography>
        </StatusStat>
        <Flex sx={{ ml: 'auto', alignItems: 'center', gap: '12px' }}>
          <RecheckButtonComponent
            isRechecking={isRechecking}
            onClick={onRecheck}
          />
          {hasIssues && (
            <DetailsToggle onClick={onToggleDetails}>
              {showDetails ? 'Hide details' : 'See details'}
            </DetailsToggle>
          )}
        </Flex>
      </SummaryRow>
      {showDetails && hasIssues && checks.length > 0 && (
        <CheckRows checks={checks} />
      )}
    </>
  );
};

type DisconnectedStateProps = {
  message: string | null;
  isRechecking: boolean;
  onRecheck: () => void;
};

const DisconnectedState = ({
  message,
  isRechecking,
  onRecheck,
}: DisconnectedStateProps) => (
  <>
    <Alert severity="error">
      <Typography sx={{ fontSize: '13px' }}>
        {message ?? DISCONNECTED_FALLBACK_MESSAGE}
      </Typography>
    </Alert>
    <SummaryRow>
      <StatusStat color="red">
        <Typography sx={{ fontSize: '13px', color: 'common.gray2' }}>
          Not connected
        </Typography>
      </StatusStat>
      <Box sx={{ ml: 'auto' }}>
        <RecheckButtonComponent
          isRechecking={isRechecking}
          onClick={onRecheck}
        />
      </Box>
    </SummaryRow>
  </>
);

const ConnectionHealthSection = ({ environmentId }: Props) => {
  const [showDetails, setShowDetails] = useState(false);
  const queryClient = useQueryClient();
  const {
    data: envHealth,
    isLoading,
    isError,
  } = useQuery([CONNECTION_HEALTH, environmentId], () =>
    fetchConnectionHealth(environmentId),
  );

  const { mutate: recheck, isLoading: isRechecking } = useMutation(
    () => revalidateConnectionHealth(environmentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CONNECTION_HEALTH, environmentId]);
      },
    },
  );

  return (
    <Wrapper>
      <SectionTitle>Connection Health</SectionTitle>
      <SectionDescription>
        Permission checks for your data warehouse connection.
      </SectionDescription>
      {isLoading ? (
        <Flex sx={{ justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Flex>
      ) : isError ? (
        <Alert severity="error">
          Failed to load connection health. Please try again later.
        </Alert>
      ) : !envHealth ? null : envHealth.status === 'disconnected' ? (
        <DisconnectedState
          message={envHealth.message}
          isRechecking={isRechecking}
          onRecheck={() => recheck()}
        />
      ) : envHealth.status === 'pending' || envHealth.checks.length === 0 ? (
        // No checks means we haven't validated this connection yet (or the
        // warehouse has no checks). Don't show a green "0 checks passing" that
        // reads as healthy — surface the neutral not-validated state instead.
        <NotValidatedState
          isRechecking={isRechecking}
          onClick={() => recheck()}
        />
      ) : (
        <HealthyIssuesState
          checks={envHealth.checks}
          totalIssues={envHealth.total_issues}
          isRechecking={isRechecking}
          onRecheck={() => recheck()}
          showDetails={showDetails}
          onToggleDetails={() => setShowDetails((prev) => !prev)}
        />
      )}
    </Wrapper>
  );
};

export default ConnectionHealthSection;
